/** Demo-only shared state linking the "chef builds product" and "procurement onboards
 * supplier" screens: when a chef flags a new product as needing a new supplier and refers
 * it to procurement (RecipeV2), a pending referral appears on the Suppliers list so the
 * story is genuinely linked rather than two independently-scripted screens. Persisted to
 * localStorage so it survives navigation/refresh during a live demo walkthrough. Everything
 * else in the demo remains standalone-seeded mock data by design (see demo plan). */

import React, { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';

export type SupplierReferralStatus = 'PendingProcurementReview' | 'Onboarded';

export interface SupplierReferral {
  id: string;
  supplierName: string;
  requestedByProduct: string;
  reason: string;
  requestedDate: string;
  status: SupplierReferralStatus;
}

interface DemoScenarioContextType {
  referrals: SupplierReferral[];
  addReferral: (input: Pick<SupplierReferral, 'supplierName' | 'requestedByProduct' | 'reason'>) => void;
  markReferralOnboarded: (id: string) => void;
}

const STORAGE_KEY = 'demo-scenario-supplier-referrals';

const DemoScenarioContext = createContext<DemoScenarioContextType | undefined>(undefined);

function loadReferrals(): SupplierReferral[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) as SupplierReferral[] : [];
  } catch {
    return [];
  }
}

interface DemoScenarioProviderProps {
  children: ReactNode;
}

export const DemoScenarioProvider: React.FC<DemoScenarioProviderProps> = ({ children }) => {
  const [referrals, setReferrals] = useState<SupplierReferral[]>(() => loadReferrals());

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(referrals));
  }, [referrals]);

  const addReferral = useCallback((input: Pick<SupplierReferral, 'supplierName' | 'requestedByProduct' | 'reason'>) => {
    setReferrals((current) => [
      ...current,
      {
        id: `referral-${Date.now()}`,
        requestedDate: new Date().toISOString(),
        status: 'PendingProcurementReview',
        ...input,
      },
    ]);
  }, []);

  const markReferralOnboarded = useCallback((id: string) => {
    setReferrals((current) => current.map((referral) => (referral.id === id ? { ...referral, status: 'Onboarded' } : referral)));
  }, []);

  return (
    <DemoScenarioContext.Provider value={{ referrals, addReferral, markReferralOnboarded }}>
      {children}
    </DemoScenarioContext.Provider>
  );
};

export function useDemoScenario(): DemoScenarioContextType {
  const context = useContext(DemoScenarioContext);
  if (!context) {
    throw new Error('useDemoScenario must be used within a DemoScenarioProvider');
  }
  return context;
}
